import { signal, computed, ReadonlySignal, effect } from "@preact/signals-core";

// 1. Raw State
export const productData = signal<any>(null); // Populated from Liquid
export const selectedVariantId = signal<number | null>(null);
export const activeMediaId = signal<number | null>(null);
export const quantity = signal(1);
export const selectedQuantity = signal(1); // New signal for selected quantity

// 2. Computed State (The "Selectors")
export const productMedia = computed(() => productData.value?.media ?? []);

export const shipsBy = computed(() => {
  const tags = productData.value?.tags ?? [];
  const tag = tags.find((t: string) => t.startsWith("ships_by-"));
  const fallback = (window as any).defaultShippingDate as string | undefined;
  return tag ? tag.replace("ships_by-", "") : (fallback || null);
});

export const filteredTags = computed(() => {
  const tags = productData.value?.tags ?? [];
  return tags.filter((t: string) => !t.startsWith("ships_by-"));
});

export const selectedVariant = computed(() => {
  if (!productData.value || !selectedVariantId.value) {
    // If no variant is selected, but we have product data, return the first variant
    if (productData.value?.variants) {
      return productData.value.variants[0];
    }
    return null;
  }
  return productData.value.variants.find(
    (v: any) => v.id === selectedVariantId.value
  );
});

// Signal to compute badges for each variant
export const variantBadges = computed(() => {
  if (!productData.value) return [];

  return productData.value.variants.map((variant: any) => {
    const badge = computeBadge(variant.price, variant.compare_at_price);
    return {
      variantId: variant.id,
      type: badge.type,
      value: badge.value,
    };
  });
});

export const isAvailable = computed(() => {
  const variant = selectedVariant.value;
  if (!variant) return false;
  // Gift cards don't track inventory — Shopify reports qty 0 while available is true
  if (productData.value?.type === "Gift Card") {
    return Boolean(variant.available);
  }
  // Use inventory_quantity instead of available flag to match collection store behavior
  // This ensures "continue selling when out of stock" doesn't prevent extended offer from showing
  const qty = variant.inventory_quantity ?? variant.inventoryQuantity ?? 0;
  return qty > 0;
});
export const currentPrice = computed(
  () => selectedVariant.value?.price ?? productData.value?.price
);
export const compareAtPrice: ReadonlySignal<string | null> = computed(
  () => selectedVariant.value?.compare_at_price?.toString() ?? null
);

// 3. Effects (reactions to state changes)

// Initialize IDs when productData is available
effect(() => {
  if (productData.value) {
    // Set initial selected variant if not already set
    if (!selectedVariantId.value) {
      selectedVariantId.value = productData.value.variants[0]?.id ?? null;
    }
    // Set initial active media if not already set
    if (!activeMediaId.value) {
      activeMediaId.value =
        selectedVariant.value?.featured_media?.id ??
        productData.value.media[0]?.id ??
        null;
    }
  }
});

// Reset quantity when variant changes
effect(() => {
  if (selectedVariantId.value) {
    selectedQuantity.value = 1;
  }
});

// Sync active media when the selected variant changes.
// peek() reads activeMediaId without subscribing to it, so gallery arrow
// navigation never triggers this effect and causes a reset loop.
effect(() => {
  const variant = selectedVariant.value;
  if (
    variant?.featured_media &&
    activeMediaId.peek() !== variant.featured_media.id
  ) {
    activeMediaId.value = variant.featured_media.id;
  }
});

// 4. Actions
export const changeVariant = (id: number) => {
  selectedVariantId.value = id;
  syncUrlWithVariant(id);
};

export const updateQuantity = (newQuantity: number) => {
  selectedQuantity.value = newQuantity; // Action to update selected quantity
};

export const setActiveMedia = (mediaId: number) => {
  activeMediaId.value = mediaId;

  // If this media is a featured image for a variant, update the selected variant
  const variant = productData.value?.variants.find(
    (v: any) => v.featured_media?.id === mediaId
  );
  if (variant && selectedVariantId.value !== variant.id) {
    selectedVariantId.value = variant.id;
  }
};

// Helper: Sync URL with selected variant for shareability
export const syncUrlWithVariant = (variantId: number) => {
  const url = new URL(window.location.href);
  url.searchParams.set("variant", variantId.toString());
  window.history.replaceState({}, "", url.toString());
};

// 5. Helper Functions
// Function to compute the badge
const computeBadge = (price: number, compareAtPrice: number | null) => {
  if (!compareAtPrice) {
    // No compare_at_price -> Exclusive Offer
    return { type: "exclusive-offer", value: null };
  }

  const raw = ((compareAtPrice - price) / compareAtPrice) * 100;
  const percentageSaved = (window as any).alwaysRoundDown
    ? Math.floor(raw)
    : Math.round(raw);

  if (percentageSaved <= 20) {
    // Less than 20% saved -> Special Offer
    return { type: "special-offer", value: null };
  }

  // 20% or more saved -> Regular Offer
  return { type: "regular", value: percentageSaved };
};
